<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { initialsOf, isHexColor, onColor } from '../brand'

// A tenant's mark: its logo, or the initials on a rounded tile in the brand colour (text in
// black or white, whichever has more contrast). Decorative: the name is always shown next to it.
const props = withDefaults(
  defineProps<{ name: string; logoUrl?: string | null; color?: string | null; initials?: string | null; size?: number }>(),
  { logoUrl: null, color: null, initials: null, size: 40 },
)

const failed = ref(false)
watch(
  () => props.logoUrl,
  () => (failed.value = false),
)

const radius = computed(() => `${Math.round(props.size / 4)}px`)
const tileStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  minWidth: `${props.size}px`,
  borderRadius: radius.value,
  fontSize: `${Math.max(11, Math.round(props.size * 0.37))}px`,
  ...(isHexColor(props.color) ? { background: props.color, color: onColor(props.color) } : {}),
}))
const text = computed(() => props.initials || initialsOf(props.name))
</script>

<template>
  <img
    v-if="logoUrl && !failed"
    :src="logoUrl"
    alt=""
    class="ui-brand-mark ui-brand-mark--logo"
    :style="{ width: `${size}px`, height: `${size}px`, minWidth: `${size}px`, borderRadius: radius }"
    @error="failed = true"
  />
  <span v-else aria-hidden="true" class="ui-brand-mark" :style="tileStyle">{{ text }}</span>
</template>

<style scoped>
.ui-brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--on-accent, #ffffff);
  font-weight: var(--weight-bold);
  letter-spacing: 0.02em;
  flex-shrink: 0;
}
.ui-brand-mark--logo {
  object-fit: contain;
  background: transparent;
}
</style>
